import React, { useMemo } from 'react';
import { useAuth } from './auth';
import { getAdminAppUrl, getDealerAppUrl, getNotaryAppUrl } from '~/helpers/utilHelper';

export const perms = {
  view_my_account: 'view_my_account',
  edit_my_account: 'edit_my_account',
  view_scheduler_us_region: 'view_scheduler_us_region',
  edit_scheduler_us_region: 'edit_scheduler_us_region',
  view_out_of_office: 'view_out_of_office',
};

type AccessContextValue = {
  iAmGranted: (attribute: string, subject?: any) => boolean;
  iAmNotGranted: (attribute: string, subject?: any) => boolean;
  isAnyGranted: (attributes: string[], subject?: any) => boolean;
  iAmAdmin: () => boolean;
  iAmScheduler: () => boolean;
  iAmDealerGroupManager: () => boolean;
  iAmDealerRegionalManager: () => boolean;
  iAmDealerStoreManager: () => boolean;
  iAmDealerFinanceManager: () => boolean;
  iAmDealerSalesManager: () => boolean;
  iAmDealerUpperManager: () => boolean;
  iAmDealerLowerManager: () => boolean;
  iAmDealerManager: () => boolean;
  iAmDealerStoreUser: () => boolean;
  iAmAdminType: () => boolean;
  iAmDealerType: () => boolean;
  getUserAppUrl: (fragment?: string) => string;
};

const AccessContext = React.createContext<AccessContextValue>({} as AccessContextValue);

const AccessProvider = (props: any) => {

  const { user: authUser } = useAuth();

  // all function definitions should go inside the useMemo callback so they're only recreated when authUser changes
  // right now authUser is the only dependency
  const contextValue = useMemo(() => {

    const iAmAdmin = () => authUser && authUser.isAdmin();
    const iAmScheduler = () => authUser && authUser.isScheduler();
    const iAmDealerGroupManager = () => authUser && authUser.isDealerGroupManager();
    const iAmDealerRegionalManager = () => authUser && authUser.isDealerRegionalManager();
    const iAmDealerStoreManager = () => authUser && authUser.isDealerStoreManager();
    const iAmDealerFinanceManager = () => authUser && authUser.isDealerFinanceManager();
    const iAmDealerSalesManager = () => authUser && authUser.isDealerSalesManager();
    const iAmDealerUpperManager = () => authUser && authUser.isDealerUpperManager();
    const iAmDealerLowerManager = () => authUser && authUser.isDealerLowerManager();
    const iAmDealerManager = () => authUser && authUser.isDealerManager();
    const iAmDealerStoreUser = () => authUser && authUser.isDealerStoreUser();

    const iAmAdminType = () => authUser && authUser.isAdminType();
    const iAmDealerType = () => authUser && authUser.isDealerType();
    const iAmNotaryType = () => authUser && authUser.isNotaryType();

    const iAmGranted = (attribute: string, _subject?: any) => {
      switch (attribute) {
        case perms.view_my_account:
        case perms.edit_my_account:
          return true;
        case perms.view_scheduler_us_region:
        case perms.edit_scheduler_us_region:
          return authUser?.isScheduler() || authUser?.isLeadOps();
        case perms.view_out_of_office:
          return authUser?.isScheduler();
        default:
          return false;
      }
    };

    const iAmNotGranted = (attribute: string, _subject?: any) => !iAmGranted(attribute, _subject);

    const isAnyGranted = (attributes: string[], _subject?: any) => attributes.some(attr => iAmGranted(attr, _subject));

    const getUserAppUrl = (fragment?: string) => {
      if (iAmAdminType()) {
        return getAdminAppUrl(fragment);
      } else if (iAmDealerType()) {
        return getDealerAppUrl(fragment);
      } else if (iAmNotaryType()) {
        return getNotaryAppUrl(fragment);
      }
      return "";
    };

    return {
      iAmGranted,
      iAmNotGranted,
      isAnyGranted,
      iAmAdmin,
      iAmScheduler,
      iAmDealerGroupManager,
      iAmDealerRegionalManager,
      iAmDealerStoreManager,
      iAmDealerFinanceManager,
      iAmDealerSalesManager,
      iAmDealerUpperManager,
      iAmDealerLowerManager,
      iAmDealerManager,
      iAmDealerStoreUser,
      iAmAdminType,
      iAmDealerType,
      getUserAppUrl,
    };

  }, [authUser]);

  return <AccessContext.Provider value={contextValue} {...props} />
}

// helper hook that makes context data available
export const useAccess = () => React.useContext(AccessContext);

export default AccessProvider;